import type { CompetitorFilters } from '../types/dashboard.types';

export type ExploreApiPrefix = '/dashboard' | '/overview' | '/drilldown';

export interface CompetitorFilterDefaultsData {
  defaultBrand: string | null;
  defaultIndustry: string | null;
  competitors: Array<{
    industry: string;
    mainCompetitor: string | null;
    industryCompetitors: string | null;
    /** Admin-set My Brand for this industry (AD-2790). Optional while the API rolls out. */
    defaultBrand?: string | null;
  }>;
}

export interface CompetitorSettingsRow {
  industry: string;
  mainCompetitor: string | null;
  industryCompetitors: string | null;
}

export interface BootstrapCompetitorFilterDefaultsPayload {
  myIndustry: string;
  myBrand: string;
  competitorsIndustry: string;
  mainCompetitor: string;
  benchmark: string[];
}

export function resolveStartingIndustry(args: {
  defaultIndustry: string | null | undefined;
  industryIndexes: string[];
}): string | null {
  const { defaultIndustry, industryIndexes } = args;
  if (industryIndexes.length === 0) return null;
  if (defaultIndustry && industryIndexes.includes(defaultIndustry)) {
    return defaultIndustry;
  }
  return industryIndexes[0] ?? null;
}

/**
 * Replaces a My Brand that is not in its industry's roster — from a preset, a deep
 * link, or state hydrated before the roster loaded — with the industry default.
 * Filters for a different industry than the roster are returned unchanged.
 */
export function pruneMyBrandToRoster(args: {
  filters: CompetitorFilters;
  rosterIndustry: string | null;
  rosterBrandNames: readonly string[];
  fallbackBrand: string | null;
}): CompetitorFilters {
  const { filters, rosterIndustry, rosterBrandNames, fallbackBrand } = args;
  if (filters.myIndustry !== rosterIndustry || filters.myBrand === null) return filters;
  if (rosterBrandNames.includes(filters.myBrand)) return filters;
  return { ...filters, myBrand: fallbackBrand };
}

export function findCompetitorSettingsForIndustry(
  rows: CompetitorSettingsRow[],
  industry: string | null,
): CompetitorSettingsRow | undefined {
  if (!industry) return undefined;
  return rows.find((row) => row.industry === industry);
}

export function parseIndustryCompetitors(csv: string | null | undefined, validNames: string[]): string[] {
  if (!csv) return [];
  const allowed = new Set(validNames);
  return csv
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value && allowed.has(value));
}

export function seedMainCompetitorFromSettings(
  saved: CompetitorSettingsRow | undefined,
  validNames: string[],
): string | null {
  if (!saved?.mainCompetitor) return null;
  return validNames.includes(saved.mainCompetitor) ? saved.mainCompetitor : null;
}

export function seedBenchmarkFromSettings(
  saved: CompetitorSettingsRow | undefined,
  validNames: string[],
  fallbackAllNames: string[],
): string[] {
  const parsed = parseIndustryCompetitors(saved?.industryCompetitors, validNames);
  if (parsed.length > 0) return parsed;
  return fallbackAllNames;
}

/** Case-insensitive BQ key match on the raw `competitor` key. */
export function findBrandImageUrl(
  brands: readonly { name: string; imageUrl?: string | null }[],
  brandName: string | null | undefined,
): string | null {
  if (!brandName) return null;
  const key = brandName.trim().toLowerCase();
  if (!key) return null;
  const match = brands.find((brand) => brand.name.trim().toLowerCase() === key);
  return match?.imageUrl ?? null;
}

type VolumeRankedBrand = { name: string; assetCount90d?: number | null };

/** Highest 90-day creative count first; missing counts last; name as the tie-break. */
export function sortBrandsByVolumeDesc<T extends VolumeRankedBrand>(brands: readonly T[]): T[] {
  return [...brands].sort((a, b) => {
    const aCount = a.assetCount90d ?? -1;
    const bCount = b.assetCount90d ?? -1;
    if (bCount !== aCount) return bCount - aCount;
    return a.name.localeCompare(b.name);
  });
}

export function pickHighestVolumeBrandName(
  brands: Array<{ name: string; assetCount90d?: number | null }>,
): string | null {
  return sortBrandsByVolumeDesc(brands)[0]?.name ?? null;
}

export function resolveCompetitorBrandSelections(args: {
  brands: Array<{ name: string; assetCount90d?: number | null }>;
  mainCompetitor: string | null;
  benchmark: string[];
  saved: CompetitorSettingsRow | undefined;
  hasUrlMainCompetitor: boolean;
  hasUrlBenchmark: boolean;
}): { mainCompetitor: string | null; benchmark: string[] } {
  const { brands, saved } = args;
  if (brands.length === 0) {
    return { mainCompetitor: null, benchmark: [] };
  }

  const validNames = brands.map((brand) => brand.name);
  const fallbackMain =
    seedMainCompetitorFromSettings(saved, validNames) ?? pickHighestVolumeBrandName(brands);

  let mainCompetitor = args.mainCompetitor;
  if (mainCompetitor && !validNames.includes(mainCompetitor)) {
    mainCompetitor = fallbackMain;
  } else if (!mainCompetitor && !args.hasUrlMainCompetitor) {
    mainCompetitor = fallbackMain;
  }

  const prunedBenchmark = args.benchmark.filter((name) => validNames.includes(name));
  const lostAllFromPrune = args.benchmark.length > 0 && prunedBenchmark.length === 0;
  const mainIsValid = args.mainCompetitor !== null && validNames.includes(args.mainCompetitor);
  const shouldSeedBenchmark =
    prunedBenchmark.length === 0 &&
    !args.hasUrlBenchmark &&
    (lostAllFromPrune || !mainIsValid);
  const benchmark = shouldSeedBenchmark
    ? seedBenchmarkFromSettings(saved, validNames, validNames)
    : prunedBenchmark;

  return { mainCompetitor, benchmark };
}

export function buildBootstrapPayload(args: {
  defaults: CompetitorFilterDefaultsData | undefined;
  appliedFilters: CompetitorFilters;
}): BootstrapCompetitorFilterDefaultsPayload | null {
  const { defaults, appliedFilters } = args;
  const {
    myIndustry,
    myBrand,
    competitorsIndustry,
    mainCompetitor,
    benchmark,
  } = appliedFilters;

  if (!myIndustry || !myBrand || !competitorsIndustry || !mainCompetitor || benchmark.length === 0) {
    return null;
  }

  const industryKey = competitorsIndustry || myIndustry;
  const competitorRow = findCompetitorSettingsForIndustry(defaults?.competitors ?? [], industryKey);
  const hasIndustryDefaultBrand = !!defaults?.competitors.find((row) => row.industry === myIndustry)
    ?.defaultBrand;

  const needsTenantBrand = !defaults?.defaultBrand && !!myBrand && !hasIndustryDefaultBrand;
  const needsTenantIndustry = !defaults?.defaultIndustry && !!myIndustry;
  const needsMainCompetitor = !competitorRow?.mainCompetitor && !!mainCompetitor;
  const needsBenchmark = !competitorRow?.industryCompetitors && benchmark.length > 0;

  if (!needsTenantBrand && !needsTenantIndustry && !needsMainCompetitor && !needsBenchmark) {
    return null;
  }

  return {
    myIndustry,
    myBrand,
    competitorsIndustry,
    mainCompetitor,
    benchmark,
  };
}
